/* eslint-disable import/no-cycle */
import jwtDecode from 'jwt-decode';
import { i18n } from '@/boot/i18n';
import { clearSentryUser, setSentryUser } from '@/boot/sentry';
import router from '@/router/index';
import AuthService from '@/services/auth';
import http from '@/tools/http';
import store from '@/utils/store';
import * as types from './mutationTypes';
import { getDefaultError } from './authState';

const mapperErrors = {
  'Robot detected': 'login.messages.robot_detected',
  'Number of attempts achieved, wait for 5 minutes and try again': 'login.messages.number_of_attempts_achieved',
  'Problem validating recaptcha': 'login.messages.problem_validating_recaptcha',
  'Problem validating Oauth Token': 'problem_validating_oauth_token',
};

let timeToRefreshToken = null;

function scheduleRefreshToken(token) {
  const decoded = jwtDecode(token);

  const tokenExpiresIn = decoded.exp * 1000;
  const now = new Date().getTime();
  const timeToRefreshTokenInMs = (tokenExpiresIn - now) - (60 * 1000);

  if (timeToRefreshToken) {
    clearTimeout(timeToRefreshToken);
  }

  timeToRefreshToken = setTimeout(() => {
    store.dispatchAction('auth', 'refreshToken');
  }, timeToRefreshTokenInMs < 0 ? (15 * 1000) : timeToRefreshTokenInMs);
}

export default {
  initState({ commit }) {
    commit(types.SET_LOGIN_ERROR, getDefaultError());
  },
  clearState({ commit }) {
    commit(types.CLEAR_LOGIN_ERROR);
  },
  setSessionOff({ commit }, sessionOff) {
    commit(types.SESSION_OFF, sessionOff);
  },
  async login({ dispatch, commit }, data) {
    try {
      const response = await AuthService.login(data);

      if (response.data.data) {
        await dispatch('setLoginSuccess', response.data.data.access_token);
      }
      commit(types.CLEAR_LOGIN_ERROR);
    } catch (error) {
      dispatch('setLoginError', { error });
    }
  },
  async loginOAuth({ dispatch, commit }, data) {
    try {
      const response = await AuthService.loginOAuth(data);
      if (response.data.data) {
        await dispatch('setLoginSuccess', response.data.data.access_token);
      }
      commit(types.CLEAR_LOGIN_ERROR);
    } catch (error) {
      dispatch('setLoginError', { error, customMessage: i18n.t('login.messages.oauth_fail') });
    }
  },
  async loginSSO({ dispatch, commit }, token) {
    try {
      const response = await AuthService.loginSSO(token);
      if (response.data.data) {
        await dispatch('setLoginSuccess', response.data.data.access_token);
      }
      commit(types.CLEAR_LOGIN_ERROR);
    } catch (error) {
      dispatch('setLoginError', { error });
    }
  },
  async logoff({ commit, dispatch }) {
    try {
      await AuthService.logoff();
    } finally {
      commit(types.LOGOFF);
      dispatch('profile/clearProfile', null, { root: true });
      clearSentryUser();
      delete http.defaults.headers.common.Authorization;
      clearTimeout(timeToRefreshToken);
      router.push('/login').catch(() => {});
    }
  },
  async refreshToken({ dispatch }) {
    try {
      const response = await AuthService.refreshToken();
      if (response.data.data) {
        await dispatch('setToken', response.data.data.access_token);
      }
    } catch {
      dispatch('setSessionOff', true);
      dispatch('logoff');
    }
  },
  async setToken({ commit }, token) {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('t-token', token);
        http.defaults.headers.common.Authorization = `Bearer ${token}`;
      }
    } finally {
      scheduleRefreshToken(token);

      commit(types.SET_TOKEN, token);
    }
  },
  async setLoginSuccess({ dispatch }, token) {
    await dispatch('setToken', token);
    await dispatch('profile/getOne', null, { root: true });
  },
  setLoginError({ commit }, { error, customMessage }) {
    let message = i18n.t('login.messages.invalid_access');

    if (customMessage) {
      message = customMessage;
    }

    if (error.response) {
      const mapperError = mapperErrors[error.response.data.data];
      message = mapperError ? i18n.t(mapperError) : message;
    }

    commit(types.SET_LOGIN_ERROR, {
      hasError: true,
      message,
    });
  },
  async clearStorage({ dispatch, commit }) {
    const token = localStorage.getItem('t-token');
    if (token) {
      await dispatch('setLoginSuccess', token);
      commit(types.CLEAR_LOGIN_ERROR);

      return token;
    }

    commit(types.CLEAR_STORAGE);
    return null;
  },
  checkAuth({ dispatch }) {
    const token = localStorage.getItem('t-token');
    if (token) {
      dispatch('setToken', token);

      const { email, name, uuid } = Object.prototype.hasOwnProperty.call(localStorage, 't-profile')
        ? JSON.parse(localStorage.getItem('t-profile'))
        : {};

      const { name: organization = null } = Object.prototype.hasOwnProperty.call(localStorage, 't-org')
        ? JSON.parse(localStorage.getItem('t-org'))
        : {};

      setSentryUser({
        email, name, uuid, organization,
      });
    }
  },
  clearTimeToRefreshToken() {
    clearTimeout(timeToRefreshToken);
  },
};
